import { CODE_WORLD_CATALOGUE, worldIn } from './world-catalogue.js';
import { VILLA_SPACE, START_GAP, placePose, startPoses } from './placement.js';
/**
 * A school's furniture in the villa (ADR-037, docs/superpowers/specs/2026-09-30-villa-furnishing-design.md):
 * the limits, the materials, a piece's pose and where it starts, and the shapes the session sends.
 * Pure and shared, so the API, the headset and the console's viewer read one definition.
 */
export const FURNITURE_LIMITS = {
    piecesPerSet: 40,
    setsPerTenant: 20,
    /** An uploaded STL. */
    maxBytes: 20 * 1024 * 1024,
    /** Before simplification (a 20 MB binary STL holds 419,428). */
    maxSourceTriangles: 500_000,
    /** After simplification (decision 5). */
    pieceTriangles: 6_000,
    /** After crease splitting; Uint16 indices. */
    maxVertices: 20_000,
    maxGlbBytes: 512 * 1024,
    /** Metres, the largest dimension; outside them the console asks "Hoe hoog is dit stuk in het echt?". */
    minSize: 0.1,
    maxSize: 4.0,
    /** Metres, the teacher's answer to that question. */
    minHeight: 0.05,
    maxHeight: 3.0,
    titleMax: 40,
    setTitleMax: 60,
};
/** `plain` is the light grey a piece starts in, not on the palette; then the palette's eight. */
export const MATERIALS = [
    'plain',
    'light-wood',
    'dark-wood',
    'white',
    'black',
    'red',
    'blue',
    'green',
    'metal',
];
export const PALETTE = MATERIALS.slice(1);
export function isMaterial(value) {
    return typeof value === 'string' && MATERIALS.includes(value);
}
export const FURNISH_RATES = {
    /** A holder sends at most this often, and only when the piece moved. */
    holdHz: 10,
    /** …and at least this often while holding still. */
    holdKeepaliveMs: 2000,
    /** Holds closer together than this are dropped (a 20 Hz cap). */
    serverMinMs: 50,
    /** The hub batches moves to other headsets this often. */
    relayMs: 100,
    /** A hold with no hold or drop for this long ends where it is. */
    holdIdleSeconds: 10,
    /** Other headsets draw a moving piece this far behind. */
    interpolationMs: 120,
    /** A grab with no answer is given up. */
    grabTimeoutMs: 2000,
    turnSnapDegrees: 15,
};
/** Metres to the centimetre, degrees to the degree, 0–359. The wire and the database hold only these. */
export function roundPose(pose) {
    const cm = (metres) => Math.round(metres * 100) / 100 + 0;
    const turn = (((Math.round(pose.turn) % 360) + 360) % 360) + 0;
    const rounded = {
        at: [cm(pose.at[0]), cm(pose.at[1])],
        turn,
        material: pose.material,
    };
    // A height is kept only when there is one: a pose on the floor reads and writes as it always did.
    if (typeof pose.y === 'number' && Number.isFinite(pose.y) && cm(pose.y) > 0)
        rounded.y = cm(pose.y);
    return rounded;
}
/** How the garage is filled at the start (spec, "The plan"): VILLA_SPACE.start, packed by startPoses. */
export const GARAGE_PACKING = {
    /** The first row's front: 2.4 m clear of the garage's viewpoint, so the lectern stands free. */
    fromX: 4.2,
    /** Between two pieces; half of it is kept from a wall. */
    gap: START_GAP,
    /** Every piece faces the garage's viewpoint: its front (+z) turned to −x. */
    turn: 270,
};
/**
 * The garage's slots for pieces of these sizes ([width, height, depth], in the set's order), in the
 * same order: shelf-packed in rows across the garage from x = 4.2, deepest first, 0.4 m apart, each
 * turned to face the garage's viewpoint; then the driveway; then the lawn. A piece that fits nowhere
 * any more starts the three areas again, over the pieces already there. (startPoses of VILLA_SPACE.)
 */
export function garageSlots(sizes) {
    return startPoses(VILLA_SPACE, sizes).map((slot) => roundPose({ ...slot, material: 'plain' }));
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** A saved pose read defensively (it comes out of jsonb): rounded and clamped, or undefined. */
function savedPose(value, space, size) {
    if (!isRecord(value))
        return undefined;
    const { at, turn, material, y } = value;
    if (!Array.isArray(at) || at.length !== 2)
        return undefined;
    const [x, z] = at;
    if (typeof x !== 'number' ||
        typeof z !== 'number' ||
        !Number.isFinite(x) ||
        !Number.isFinite(z)) {
        return undefined;
    }
    if (typeof turn !== 'number' || !Number.isFinite(turn) || !isMaterial(material))
        return undefined;
    // A raised pose stays on its table or wall when the space has one that holds it, else goes to the floor.
    const placed = placePose(space, { at: [x, z], y: typeof y === 'number' ? y : 0 }, size);
    return roundPose({ at: placed.at, turn, material, y: placed.y });
}
/**
 * Where each piece stands when a session starts, or a result is drawn: its saved pose where the
 * arrangement has one (rounded, clamped to the floor), else its garage slot among the set's pieces
 * (`pieces` in the set's order). Entries for pieces no longer in the set are left out.
 */
export function posesFor(pieces, saved = {}, space = VILLA_SPACE) {
    const slots = startPoses(space, pieces.map((piece) => piece.size)).map((slot) => roundPose({ ...slot, material: 'plain' }));
    const poses = {};
    pieces.forEach((piece, i) => {
        const own = Object.hasOwn(saved, piece.id)
            ? savedPose(saved[piece.id], space, piece.size)
            : undefined;
        poses[piece.id] = own ?? slots[i];
    });
    return poses;
}
/**
 * The furnishing messages (spec, "The session protocol"; docs/05): a headset grabs, holds and drops
 * a piece; the console locks and resets the villa; the API sends the whole state and batched moves.
 */
export const FURNISH_DEVICE_MESSAGES = ['device.grab', 'device.hold', 'device.drop'];
export const FURNISH_TEACHER_MESSAGES = ['teacher.furnishLock', 'teacher.furnishReset'];
export const FURNISH_SERVER_MESSAGES = ['session.furniture', 'session.moves'];
/** Why a grab was refused: the `error` code sent to that headset, `replyTo` the grab. */
export const GRAB_REFUSALS = [
    'piece-taken',
    'holding-another',
    'villa-locked',
    'paused',
    'not-furnishing',
    'no-furniture',
    'too-big',
];
/** Piece ids are uuids; anything longer is not one of ours. */
const PIECE_ID_MAX = 64;
const fail = (error) => ({ ok: false, error });
const isPieceId = (value) => typeof value === 'string' && value.length > 0 && value.length <= PIECE_ID_MAX;
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
export function parseGrab(payload) {
    if (!isRecord(payload))
        return fail('payload must be an object');
    if (!isPieceId(payload['pieceId']))
        return fail('pieceId must be a piece id');
    return { ok: true, value: { pieceId: payload['pieceId'] } };
}
/** A hold or a drop: rounded by `roundPose` (the turn modulo 360), never clamped here. */
export function parsePose(payload) {
    if (!isRecord(payload))
        return fail('payload must be an object');
    const { pieceId, at, turn, material, y } = payload;
    if (!isPieceId(pieceId))
        return fail('pieceId must be a piece id');
    if (!Array.isArray(at) || at.length !== 2 || !at.every(isFiniteNumber)) {
        return fail('at must be two finite numbers, [x, z]');
    }
    if (!isFiniteNumber(turn))
        return fail('turn must be a finite number of degrees');
    if (!isMaterial(material))
        return fail(`material must be one of: ${MATERIALS.join(', ')}`);
    if (y !== undefined && !(isFiniteNumber(y) && y >= 0 && y <= 20)) {
        return fail('y must be a finite number of metres, 0 to 20, when present');
    }
    const pose = roundPose({
        at: [at[0], at[1]],
        turn,
        material,
        ...(y === undefined ? {} : { y }),
    });
    return { ok: true, value: { pieceId, ...pose } };
}
/** Exactly `{locked: true}` or `{locked: false}`. */
export function parseFurnishLock(payload) {
    if (!isRecord(payload) ||
        Object.keys(payload).length !== 1 ||
        typeof payload['locked'] !== 'boolean') {
        return fail('teacher.furnishLock needs exactly {locked: true or false}');
    }
    return { ok: true, value: { locked: payload['locked'] } };
}
/** Exactly `{}`. */
export function parseFurnishReset(payload) {
    if (!isRecord(payload) || Object.keys(payload).length !== 0) {
        return fail('teacher.furnishReset takes an empty object');
    }
    return { ok: true, value: {} };
}
/** The code world a furnish task takes place in; any world with a placement space will do (ADR-050). */
export const FURNISH_WORLD = 'villa';
/**
 * A lesson in which the pupils place their work: a world lesson with a furnish step. The rules
 * (assessFurnishing) guarantee its world has a placement space.
 */
export function isFurnishLesson(lesson) {
    return lesson.scene.type === 'world' && furnishStepId(lesson) !== undefined;
}
/** The id of the lesson's furnish step, or undefined when it has none. */
export function furnishStepId(lesson) {
    return lesson.steps.find((step) => step.task?.type === 'furnish')?.id;
}
/**
 * The rules for furnishing (spec, "Semantic rules" 1–3), read from the raw document so an answer
 * the schema refused still says what broke: a furnish task only in a world with a placement space
 * (the villa, or a package with a plaatsruimte); a lesson there has exactly one furnish step, and no
 * objects, views, world states or 360° photos. Rule 4 (furnish needs a vr lesson) is validate.ts's,
 * beside measure's and assemble's.
 */
export function assessFurnishing(document, worlds = CODE_WORLD_CATALOGUE) {
    if (!isRecord(document))
        return [];
    const problems = [];
    const scene = document['scene'];
    const inVilla = isRecord(scene) &&
        scene['type'] === 'world' &&
        worldIn(worlds, scene['world'])?.placement === true;
    const steps = Array.isArray(document['steps']) ? document['steps'] : [];
    const nameOf = (step, index) => typeof step['id'] === 'string' ? step['id'] : String(index + 1);
    const objects = document['objects'];
    if (inVilla && isRecord(objects) && Object.keys(objects).length > 0) {
        problems.push({
            path: '/objects',
            en: "a lesson in a placement world has no objects: the pupils' work comes with the session",
            nl: 'een inrichtles heeft geen objecten: de werkstukken van de leerlingen komen met de les mee',
        });
    }
    let furnishing;
    steps.forEach((step, index) => {
        if (!isRecord(step))
            return;
        const at = `/steps/${index}`;
        const name = nameOf(step, index);
        const task = step['task'];
        if (isRecord(task) && task['type'] === 'furnish') {
            if (!inVilla) {
                problems.push({
                    path: `${at}/task`,
                    en: 'a "furnish" task needs a world with a placement space, such as the villa: scene {"type": "world", "world": "villa"}',
                    nl: `stap "${name}": inrichten kan alleen in een wereld met een plaatsruimte`,
                });
            }
            else if (furnishing !== undefined) {
                problems.push({
                    path: `${at}/task`,
                    en: `a lesson in a placement world has one "furnish" step, and "${furnishing}" is it`,
                    nl: `stap "${name}": de les richt al in bij stap "${furnishing}"`,
                });
            }
            else {
                furnishing = name;
            }
        }
        if (!inVilla)
            return;
        if (step['view'] !== undefined) {
            problems.push({
                path: `${at}/view`,
                en: 'a lesson in a placement world has no view: the pupil moves between its rooms',
                nl: `stap "${name}": hier kiest de leerling zelf een kamer, zonder view`,
            });
        }
        if (step['world'] !== undefined) {
            problems.push({
                path: `${at}/world`,
                en: 'a lesson in a placement world sets no world states: it has none',
                nl: `stap "${name}": hier zijn geen wereldstanden`,
            });
        }
        if (step['pano'] !== undefined) {
            problems.push({
                path: `${at}/pano`,
                en: 'a lesson in a placement world has no 360° photo',
                nl: `stap "${name}": hier is geen 360°-foto`,
            });
        }
    });
    if (inVilla && furnishing === undefined) {
        problems.push({
            path: '/steps',
            en: 'a lesson in a placement world needs one step with a "furnish" task',
            nl: 'de villa-les heeft geen stap waarin de leerlingen inrichten',
        });
    }
    return problems;
}
//# sourceMappingURL=furniture.js.map